import { ErrorBase, LiteClient, liteClient } from "algoliasearch/lite";
import { HttpStatusCode } from "axios";
import { useMemo } from "react";

import { useAlgoliaParameters } from "@utils/hooks/algolia/useAlgoliaParameters.hook";

import { AlgoliaTrackingService } from "@services/algolia/tracking/algoliaTracking.service";
import {
    AlgoliaClient,
    AlgoliaRequestOptions,
} from "@services/algolia/types/client.types";
import { AlgoliaSearchRequest } from "@services/algolia/types/search.types";

import { useThunkDispatch } from "@redux/dispatcher";
import {
    generateAlgoliaParameters,
    setSearchStatus,
} from "@redux/modules/value/search/search.actions";
import { SearchStatus } from "@redux/modules/value/search/search.types";

const createSearchProxyHandler =
    (searchClient: LiteClient) =>
    (apiKey: string) =>
    <TObject>(
        searchRequest: AlgoliaSearchRequest,
        requestOptions: AlgoliaRequestOptions,
    ) =>
        searchClient.searchForHits<TObject>(searchRequest, {
            ...requestOptions,
            headers: {
                ...requestOptions?.headers,
                "x-algolia-api-key": apiKey,
                "x-algolia-usertoken":
                    AlgoliaTrackingService.getAuthenticatedUserToken() ??
                    AlgoliaTrackingService.getUserToken(),
            },
        });

const useSearchClient = (): AlgoliaClient | undefined => {
    const dispatch = useThunkDispatch();
    const { appId = "", searchApiKey = "" } = useAlgoliaParameters();

    const regenerateKey = () => dispatch(generateAlgoliaParameters());
    const dispatchError = () => dispatch(setSearchStatus(SearchStatus.Error));

    return useMemo(() => {
        if (!appId || !searchApiKey) return undefined;

        // Safe assertion, AlgoliaClient adds generic arguments to untyped algolia functions
        // (eg. getRecommendations() is not typesafe because RecommentHit is Record<string, any>)
        const searchClient = liteClient(appId, searchApiKey) as AlgoliaClient;
        const searchWithApiKey = createSearchProxyHandler(searchClient);

        return {
            ...searchClient,
            searchForHits: async <TObject>(
                searchRequest: AlgoliaSearchRequest,
                requestOptions: AlgoliaRequestOptions,
            ) =>
                searchWithApiKey(searchApiKey)<TObject>(
                    searchRequest,
                    requestOptions,
                )
                    .catch(async (error: ErrorBase) => {
                        if (
                            error.status !== HttpStatusCode.BadRequest &&
                            error.status !== HttpStatusCode.Forbidden
                        ) {
                            throw error;
                        }

                        const { searchApiKey } = await regenerateKey();
                        if (!searchApiKey) throw error;

                        return searchWithApiKey(searchApiKey)<TObject>(
                            searchRequest,
                            requestOptions,
                        );
                    })
                    .catch((error: ErrorBase) => {
                        dispatchError();

                        throw error;
                    }),
        };
    }, [appId, searchApiKey]);
};

export { useSearchClient };
